import {useQueryClient} from '@tanstack/react-query';
import {useRouter} from 'next/router';
import {
  ChangeEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {openCashCharge} from '../../cashcharge/openCashCharge.function';
import {PcSearchesContainer} from '../../common/containers/PcSearchesContainer';
import {t} from '../../common/getTranslations';
import {requestContext} from '../../common/requestContext';
import {twMerge} from '../../common/twMerge';
import {useColorMode} from '../../common/useColorMode';
import {useIsDarkMode} from '../../common/useIsDarkMode.hook';
import useTypedDispatch from '../../common/useTypedDispatch';
import useTypedSelector from '../../common/useTypedSelector';
import {getTrimmedTextEvent} from '../../common/util/getTrimmedTextEvent';
import {useDialog} from '../../dialog/useDialog.hook';
import {logoutPageweb} from '../../domain/login/util/logoutPageweb';
import {GNB_MENU_LIST} from '../../domain/menu/constant';
import {setIsPcHeaderExposed} from '../../domain/menu/util/MenuSlice';
import {openAutoCharge} from '../../domain/relay/autocharge/util/openAutoCharge';
import {generateRelayRedirectUrl} from '../../domain/relay/redirect/util/generateRelayRedirectUrl';
import {SEARCH_INPUT_MAX_LENGTH} from '../../domain/search/constant';
import {SearchAutoCompleteContainer} from '../../domain/search/container/SearchAutoCompleteContainer';
import {useGetSearchKeywordQueryData} from '../../domain/search/hook/useGetSearchKeywordQueryData';
import {useOpenPcSearchModal} from '../../domain/search/hook/useOpenPcSearchModal';
import {useSearchInputKeyword} from '../../domain/search/hook/useSearchInputKeyword';
import {setOpenAutoComplete} from '../../domain/search/state/searchSlice';
import {selectAutoCompleteCustomProps} from '../../domain/search/state/selector';
import {createSearchKeywordInput} from '../../domain/search/util/createSearchKeywordInput';
import {commonErrorHandler} from '../../errors/commonErrorHandler';
import {getSerializedPagewebError} from '../../errors/getSerializedPagewebError.function';
import {aggregateAndSendLog} from '../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../logging/logEvent';
import {putTiaraObjectProps} from '../../logging/putTiaraObjectProps';
import {
  PROFILE_MENU_CLICK_ACTION_NAME_MAP,
  ProfileMenuId,
} from '../../profileMenu/constants';
import {CACHE_ONLY_CONFIG} from '../../reactQuery/constant';
import {ReactQueryFetcher} from '../../reactQuery/ReactQueryFetcher';
import {getAutoSaveRecentKeyword} from '../../recentSearchKeyword/autoSaveRecentKeyword.storage';
import {addRecentKeyword} from '../../recentSearchKeyword/recentKeywordList.storage';
import {useLogin} from '../../user/useLogin.hook';
import {useInvenMyInfoQuery} from '../../wrapper';
import {useActivateUserQuery} from '../../wrapper/authWrapper';
import {
  useInfiniteSearchKeywordQuery,
  useSearchAutoCompleteQuery,
} from '../../wrapper/searchWrapper';
import {useUserCashQuery} from '../../wrapper/storeWrapper';
import {Sticky} from '../atoms/Sticky/Sticky';
import {StickyLocation} from '../atoms/Sticky/StickyLocation.enum';
import {PcHeader} from '../organisms/PcHeader/PcHeader';
import {
  PC_PROFILE_MENU_LIST,
  PC_PROFILE_MENU_MAP,
} from '../organisms/PcProfileMenu/constants';
import {PcProfileMenu} from '../organisms/PcProfileMenu/PcProfileMenu';
import {PcProfileMenuItem} from '../organisms/PcProfileMenu/PcProfileMenuItem.interface';
import {PcSearchesModal} from '../organisms/PcSearchesModal/PcSearchesModal';

export function PcLayoutHeader() {
  const dispatch = useTypedDispatch();
  const router = useRouter();
  const {refetch: refetchUserCash, data: cashData} = useUserCashQuery(
    undefined,
    {
      enabled: false,
    }
  );

  const {refetch: refetchInvenMyInfo, data: invenMyInfoData} =
    useInvenMyInfoQuery(undefined, {enabled: false});

  const remainCash = cashData?.userCash?.remainCash;

  const {selectedGnbMenuId, profileUpdatedMenus, user, isGiftUpdated} =
    useTypedSelector(state => ({
      selectedGnbMenuId: state.menu.selectedGnbMenuId,
      profileUpdatedMenus: state.profileMenu.updatedMenus,
      user: state.user.user,
      isGiftUpdated: state.todayGift.isUpdated,
    }));

  const {searchKeyword} = useTypedSelector(state => state.search);
  const {inputKeyword, setInputKeyword, debouncedOnChangeInputKeyword} =
    useSearchInputKeyword();
  const {openAutoComplete} = useTypedSelector(state => state.search);

  const {isMobile} = requestContext.requestInfo.display;
  const {openPcSearchModal, setOpenSearchModal} = useOpenPcSearchModal();
  const {isLogin, doLogin} = useLogin();
  const {DialogWrapper, Dialog, showDialog, hideDialog} = useDialog({
    isMobile,
  });

  const openSearchModal = useCallback(() => {
    setOpenSearchModal(true);
  }, [setOpenSearchModal]);

  const onChangeKeyword = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!openPcSearchModal) {
        openSearchModal();
      }

      let value = '';
      if (requestContext.requestInfo.display.isAndroid) {
        const ev = getTrimmedTextEvent(event, SEARCH_INPUT_MAX_LENGTH);
        value = ev.target.value;
      } else {
        value = event.target.value;
      }
      setInputKeyword(value);
      debouncedOnChangeInputKeyword(value);
    },
    [
      debouncedOnChangeInputKeyword,
      openPcSearchModal,
      openSearchModal,
      setInputKeyword,
    ]
  );
  const queryClient = useQueryClient();
  const onSubmitKeyword = useCallback(async () => {
    if (!inputKeyword) return;
    getAutoSaveRecentKeyword() && addRecentKeyword(inputKeyword);
    if (router.query.keyword === inputKeyword) {
      await queryClient.resetQueries({
        queryKey: useInfiniteSearchKeywordQuery.getKey({
          input: createSearchKeywordInput({keyword: inputKeyword}),
        }),
      });
    }
    router.push({
      pathname: '/search/result',
      query: {keyword: inputKeyword},
    });
  }, [inputKeyword, queryClient, router]);

  const {searchInfiniteData} = useGetSearchKeywordQueryData();
  const autoCompleteCustomProps =
    selectAutoCompleteCustomProps(searchInfiniteData);

  const onClickKeywordRemoveButton = useCallback(() => {
    setInputKeyword('');
    debouncedOnChangeInputKeyword('');
    openSearchModal();
  }, [debouncedOnChangeInputKeyword, openSearchModal, setInputKeyword]);

  const profileMenuList = useMemo(() => {
    return PC_PROFILE_MENU_LIST.map(menuId => ({
      ...(PC_PROFILE_MENU_MAP[menuId] as PcProfileMenuItem),
      hasDot: profileUpdatedMenus[menuId],
    }));
  }, [profileUpdatedMenus]);

  const onClickPcMenuItem = useCallback(
    (menuItem: PcProfileMenuItem, e: ReactMouseEvent) => {
      function handler() {
        if (menuItem.uid === ProfileMenuId.PassVip) {
          openAutoCharge(generateRelayRedirectUrl(window.location.href));
        }
        setIsShowProfileMenu(false);
      }
      LOG_CLICK_EVENT(
        PROFILE_MENU_CLICK_ACTION_NAME_MAP[menuItem.uid],
        e,
        handler
      );
    },
    []
  );

  const onClickNickname = useCallback((e: ReactMouseEvent) => {
    setIsShowProfileMenu(false);
    LOG_CLICK_EVENT('계정정보_클릭', e);
  }, []);

  const onClickCashCharge = useCallback((e: ReactMouseEvent) => {
    function handler() {
      openCashCharge({
        returnUrl: generateRelayRedirectUrl(window.location.href),
        cashChargeInfo: {
          cash_cvr: '더보기',
        },
      });
    }
    LOG_CLICK_EVENT('캐시충전_클릭', e, handler);
  }, []);

  const onClickLogout = useCallback(
    (e: ReactMouseEvent) => {
      LOG_CLICK_EVENT('로그아웃_클릭', e, showDialog);
    },
    [showDialog]
  );

  const onClickConfirmLogout = useCallback((e: ReactMouseEvent) => {
    async function handler() {
      await logoutPageweb({
        success: () => window.location.assign('/'),
      });
    }
    LOG_CLICK_EVENT('로그아웃_클릭', e, handler);
  }, []);

  const onClickCancelLogout = useCallback(
    (e: ReactMouseEvent) => {
      LOG_CLICK_EVENT('취소_클릭', e, hideDialog);
    },
    [hideDialog]
  );

  const profileRef = useRef<HTMLButtonElement>(null);
  const profileMenuWrapperRef = useRef<HTMLDivElement>(null);
  const [isShowProfileMenu, setIsShowProfileMenu] = useState(false);

  useEffect(() => {
    const onMouseDownHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        profileMenuWrapperRef.current &&
        !profileMenuWrapperRef.current.contains(target) &&
        !profileRef.current?.contains(target)
      ) {
        setIsShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', onMouseDownHandler);
    return () => {
      document.removeEventListener('mousedown', onMouseDownHandler);
    };
  }, []);

  useEffect(() => {
    dispatch(setIsPcHeaderExposed(true));
    return () => {
      dispatch(setIsPcHeaderExposed(false));
    };
  }, [dispatch]);

  const onClickInven = useCallback(
    (e: ReactMouseEvent) => {
      function handler() {
        if (!isLogin) {
          doLogin('/inven/recent');
          return;
        }
        router.push('/inven/recent');
      }
      LOG_CLICK_EVENT('숏컷_보관함_클릭', e, handler);
    },
    [doLogin, isLogin, router]
  );

  const onClickGift = useCallback((e: ReactMouseEvent) => {
    LOG_CLICK_EVENT('숏컷_선물함_클릭', e);
  }, []);

  const {
    data,
    error,
    refetch: activateUserQuery,
  } = useActivateUserQuery(undefined, {
    enabled: false,
  });
  const onClickProfile = useCallback(
    (e: ReactMouseEvent) => {
      async function handler() {
        // 휴면계정 활성화 페이지에서는 로그인 전에 계정 활성화를 먼저 해줘야 합니다.
        if (router.asPath.includes('inactiveaccount')) {
          if (data?.activateUser) {
            doLogin((router.query.returnUrl as string) || '/');
          } else {
            activateUserQuery();
          }
        } else if (!isLogin) {
          doLogin();
          return;
        } else {
          await refetchUserCash();
          await refetchInvenMyInfo();
          setIsShowProfileMenu(prev => !prev);
        }
      }
      LOG_CLICK_EVENT('숏컷_더보기_클릭', e, handler);
    },
    [
      refetchUserCash,
      refetchInvenMyInfo,
      activateUserQuery,
      data?.activateUser,
      doLogin,
      isLogin,
      router.asPath,
      router.query.returnUrl,
    ]
  );

  useEffect(() => {
    // 휴면계정 활성화 페이지에서 계정 활성화가 되면 다시 로그인을 해야 합니다.
    if (router.asPath.includes('inactiveaccount')) {
      if (data?.activateUser) {
        doLogin((router.query.returnUrl as string) || '/');
      } else if (error) {
        commonErrorHandler(getSerializedPagewebError(error));
      }
    }
  }, [
    data?.activateUser,
    doLogin,
    router.asPath,
    router.query.returnUrl,
    error,
  ]);

  const {toggleColorMode} = useColorMode();
  const isDarkMode = useIsDarkMode();

  const onClickToggleColorMode = useCallback(
    (e: ReactMouseEvent) => {
      LOG_CLICK_EVENT('다크모드_클릭', e, toggleColorMode);
    },
    [toggleColorMode]
  );

  const onClickSearchInput = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation();
      openSearchModal();
    },
    [openSearchModal]
  );

  useEffect(() => {
    if (inputKeyword) {
      dispatch(setOpenAutoComplete(true));
    }
  }, [dispatch, inputKeyword]);

  useEffect(() => {
    if (isShowProfileMenu) {
      aggregateAndSendLog({
        actionType: 'ViewImp',
        actionName: '팝업_노출',
        customProps: {user_action: 'imp', popup_type: '더보기팝업'},
        viewimp_contents: {
          imp_id: 'more',
          imp_provider: 'service',
          layer1: '더보기팝업',
        },
      });
    }
  }, [isShowProfileMenu]);

  return (
    <Sticky
      className="left-0 top-0 z-100 flex w-full flex-col items-center justify-center bg-theme-bg-base"
      isMobile={false}
      isWebView={false}
      location={StickyLocation.Top}
    >
      <div className="relative">
        <PcHeader
          gnbMenus={GNB_MENU_LIST}
          selectedPcGnbMenuId={selectedGnbMenuId}
          keyword={inputKeyword}
          onChangeKeyword={onChangeKeyword}
          onSubmitKeyword={onSubmitKeyword}
          onClickInven={onClickInven}
          onClickGift={onClickGift}
          shouldShowUserThumbnail={isLogin}
          userThumbnailUrl={user?.thumbnail}
          isGiftUpdated={isGiftUpdated}
          profileRef={profileRef}
          onClickProfile={onClickProfile}
          onClickKeywordRemoveButton={onClickKeywordRemoveButton}
          onClickSearchInput={onClickSearchInput}
          autoCompleteCustomProps={autoCompleteCustomProps}
          isMobile={isMobile}
        />
        <PcProfileMenu
          ref={profileMenuWrapperRef}
          profileMenuList={profileMenuList}
          nickname={user?.nickname || ''}
          cash={remainCash}
          onClickMenuItem={onClickPcMenuItem}
          onClickNickname={onClickNickname}
          onClickCashCharge={onClickCashCharge}
          onClickLogout={onClickLogout}
          className={twMerge(
            'absolute right-0 top-80pxr z-[100]',
            // 아이콘이 많아 새로 렌더링할 때 깜빡거림이 좀 있는듯하여 display none으로 처리했습니다.
            !isShowProfileMenu && 'hidden'
          )}
          isDarkMode={isDarkMode}
          autoPaymentInfo={invenMyInfoData?.myInfo?.autoPaymentInfo}
          onClickToggleColorMode={onClickToggleColorMode}
          {...putTiaraObjectProps({
            customProps: {popup_type: '더보기팝업'},
            click: {
              layer1: '더보기팝업',
              imp_id: 'more',
              imp_provider: 'service',
            },
          })}
        />
      </div>
      <PcSearchesModal open={openPcSearchModal} className="mt-18pxr">
        {!openAutoComplete && <PcSearchesContainer />}
        {openAutoComplete && (
          <ReactQueryFetcher
            reactQuery={useSearchAutoCompleteQuery}
            variables={{input: {keyword: searchKeyword}}}
            options={{
              ...CACHE_ONLY_CONFIG,
              enabled: !!searchKeyword,
            }}
            shouldRemoveCacheAfterUnmount={!searchKeyword}
            ignoreLoading={true}
            ignoreFetching={true}
          >
            {data => <SearchAutoCompleteContainer data={data} />}
          </ReactQueryFetcher>
        )}
      </PcSearchesModal>
      <DialogWrapper
        {...putTiaraObjectProps({
          customProps: {popup_type: '로그아웃_팝업'},
        })}
      >
        <Dialog.Dimmed />
        <Dialog.Title>{t('more_account_info_logout_popup')}</Dialog.Title>
        <Dialog.BottomCombination>
          <Dialog.Button onClick={onClickCancelLogout}>
            {t('common_cancel_button')}
          </Dialog.Button>
          <Dialog.Button onClick={onClickConfirmLogout}>
            {t('common_logout_button')}
          </Dialog.Button>
        </Dialog.BottomCombination>
      </DialogWrapper>
    </Sticky>
  );
}
